import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight , Lock } from 'lucide-react';
import { useBKOAcao, ACOES_CONFIG } from '@/hooks/useBKOAcao';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getTriagemDaColuna } from '@/lib/quadro-utils';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ConfirmarAcaoBKOModal } from './ConfirmarAcaoBKOModal';

interface ItemPedido {
  id: string;
  pedido_id: string;
  coluna_atual?: string;
  quadro_atual?: string;
  produto_nome?: string;
  quantidade?: number;
  subtotal?: number;
  familia?: string;
  plano_nome?: string;
  bko_criacao_id?: string;
  bko_aceite_id?: string;
  bko_input_id?: string;
  bko_tramitacao_id?: string;
}

interface BotaoAcaoBKOProps {
  item: ItemPedido;
  pedidoId: string;
  todosItens?: ItemPedido[]; // Lista de todos os itens do pedido
  onSuccess?: () => void;
  size?: 'sm' | 'default' | 'lg';
  showDescription?: boolean;
  showTriagemHint?: boolean;
  className?: string;
}

// Colunas válidas para retorno quando recuperado
const COLUNAS_VALIDAS_RETORNO = [
  'CRIAÇÃO PEDIDO',
  'ACEITE',
  'INPUT',
  'INPUT REALIZADO MOVEL',
  'INPUT REALIZADO FIXA',
  'QUALIDADE'
];

export function BotaoAcaoBKO({ 
  item, 
  pedidoId, 
  todosItens = [],
  onSuccess, 
  size = 'default',
  showDescription = false,
  showTriagemHint = true,
  className 
}: BotaoAcaoBKOProps) {
  const { 
    executarAcaoConsolidada, 
    isExecutando, 
    isExecutandoConsolidada, 
    getAcaoConfig 
  } = useBKOAcao();
  
  // Estado para modal de confirmação
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estado para coluna original (para PEDIDO RECUPERADO)
  const [colunaOriginal, setColunaOriginal] = useState<string | null>(null);
  const [loadingColuna, setLoadingColuna] = useState(false);
  
  // Normalizar coluna atual
  const colunaAtual = item.coluna_atual?.toUpperCase() || '';
  const quadroAtual = item.quadro_atual?.toUpperCase() || '';
  
  // Buscar coluna original para PEDIDO RECUPERADO
  useEffect(() => {
    if (colunaAtual === 'PEDIDO RECUPERADO') {
      setLoadingColuna(true);
      const fetchColunaOriginal = async () => {
        try {
          const { data } = await supabase
            .from('item_cancelamentos')
            .select('snapshot_anterior')
            .eq('item_id', item.id)
            .eq('recuperado', true)
            .order('recuperado_em', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          const coluna = (data?.snapshot_anterior as any)?.coluna_atual;
          if (coluna && COLUNAS_VALIDAS_RETORNO.includes(coluna)) {
            setColunaOriginal(coluna);
          } else {
            setColunaOriginal('CRIAÇÃO PEDIDO');
          }
        } catch {
          setColunaOriginal('CRIAÇÃO PEDIDO');
        } finally {
          setLoadingColuna(false);
        }
      };
      fetchColunaOriginal();
    } else {
      setColunaOriginal(null);
    }
  }, [item.id, colunaAtual]);
  
  // Verificar se está em um dos quadros válidos para ações BKO
  const quadrosValidos = ['CONTRATO_PRIME', 'TRAMITACAO_VIVO'];
  if (!quadrosValidos.includes(quadroAtual)) {
    return null;
  }
  
  // Buscar configuração da ação para a coluna atual
  const config = getAcaoConfig(colunaAtual);

  // Se não há ação disponível para esta coluna, não renderizar
  if (!config) {
    return null;
  }

  // 🔒 Coluna com triagem só se trabalha pela própria triagem (fila FIFO no cabeçalho da coluna)
  const nomeTriagem = getTriagemDaColuna(quadroAtual, colunaAtual);
  if (nomeTriagem) {
    if (!showTriagemHint) return null;
    return (
      /* R10: era Badge ÂMBAR — lido como alerta. É REGRA DE FLUXO ("é por aqui
         que se trabalha"), não aviso de problema: linha discreta em muted, com
         Lock lucide no lugar do . */
      <span className={cn('inline-flex items-center gap-1.5 text-[10px] text-muted-foreground', className)}>
        <Lock className="h-[11px] w-[11px] flex-shrink-0" />
        Trabalhe pela {nomeTriagem} — clique no cabeçalho da coluna no Kanban
      </span>
    );
  }
  
  // Determinar coluna de destino (dinâmica para PEDIDO RECUPERADO)
  const proximaColuna = colunaAtual === 'PEDIDO RECUPERADO' 
    ? (colunaOriginal || 'CRIAÇÃO PEDIDO') 
    : config.proximaColuna;

  const isLoading = isExecutando || isExecutandoConsolidada;

  const handleClick = () => {
    // Abrir modal de confirmação
    setModalOpen(true);
  };

  const handleConfirm = (itensSelecionados: string[]) => {
    setModalOpen(false);
    
    // Executar ação consolidada
    executarAcaoConsolidada(
      {
        itensIds: itensSelecionados,
        pedidoId: pedidoId,
        colunaAtual: colunaAtual
      },
      {
        onSuccess: () => {
          onSuccess?.();
        }
      }
    );
  };

  // Usar todosItens se disponível, senão criar array com item atual
  const itensParaModal = todosItens.length > 0 ? todosItens : [item];

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("flex flex-col gap-1", className)}>
              <Button
                onClick={handleClick}
                disabled={isLoading}
                size={size}
                className={cn(
                  "text-white font-medium shadow-md transition-all",
                  config.corClasse,
                  size === 'sm' && "text-xs px-3 py-1.5",
                  size === 'lg' && "text-base px-6 py-3"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <span className="mr-2">{config.icone}</span>
                    {config.label}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              {showDescription && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    → {loadingColuna ? '...' : proximaColuna}
                  </Badge>
                  {config.rpcAtribuicao && colunaAtual !== 'PEDIDO RECUPERADO' && (
                    <span className="text-[10px]">
                      (Atribui BKO {config.rpcAtribuicao.charAt(0).toUpperCase() + config.rpcAtribuicao.slice(1)})
                    </span>
                  )}
                  {colunaAtual === 'PEDIDO RECUPERADO' && colunaOriginal && (
                    <span className="text-[10px] text-primary">
                      (Retornando para etapa anterior)
                    </span>
                  )}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium">{config.label}</p>
              <p className="text-xs text-muted-foreground">
                {colunaAtual === 'PEDIDO RECUPERADO' 
                  ? `Retomar pedido para a etapa de onde foi cancelado` 
                  : config.descricao}
              </p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-muted-foreground">Próximo status:</span>
                <Badge variant="secondary" className="text-[10px]">
                  {loadingColuna ? 'Calculando...' : proximaColuna}
                </Badge>
              </div>
              {colunaAtual === 'PEDIDO RECUPERADO' && colunaOriginal && (
                <p className="text-xs text-primary">
                  ↩ Retornando para etapa original: {colunaOriginal}
                </p>
              )}
              {config.rpcAtribuicao && colunaAtual !== 'PEDIDO RECUPERADO' && (
                <p className="text-xs text-primary">
                  ℹ Atribuirá automaticamente um BKO de {config.rpcAtribuicao}
                </p>
              )}
              {config.webhookKey && (
                <p className="text-xs text-success">
                   Envia mensagem WhatsApp ao cliente
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Modal de confirmação */}
      <ConfirmarAcaoBKOModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        itemClicado={item}
        todosItens={itensParaModal}
        config={config}
        onConfirm={handleConfirm}
        isLoading={isLoading}
      />
    </>
  );
}

// Componente para exibir múltiplos itens com botões de ação
interface BotoesAcaoBKOListProps {
  itens: ItemPedido[];
  pedidoId: string;
  onSuccess?: () => void;
}

export function BotoesAcaoBKOList({ itens, pedidoId, onSuccess }: BotoesAcaoBKOListProps) {
  // Agrupar itens por coluna atual
  const itensPorColuna = itens.reduce((acc, item) => {
    const coluna = item.coluna_atual?.toUpperCase() || 'SEM_STATUS';
    if (!acc[coluna]) acc[coluna] = [];
    acc[coluna].push(item);
    return acc;
  }, {} as Record<string, ItemPedido[]>);

  // Verificar se todos os itens estão na mesma coluna (caso comum)
  const colunas = Object.keys(itensPorColuna);
  
  if (colunas.length === 1 && ACOES_CONFIG[colunas[0]]) {
    // Todos os itens na mesma coluna - mostrar um botão único
    const primeiroItem = itensPorColuna[colunas[0]][0];
    return (
      <BotaoAcaoBKO 
        item={primeiroItem} 
        pedidoId={pedidoId}
        todosItens={itens} // Passar todos os itens para o modal
        onSuccess={onSuccess}
        showDescription={true}
      />
    );
  }

  // Itens em colunas diferentes - mostrar botões individuais
  return (
    <div className="space-y-2">
      {itens.map(item => (
        <BotaoAcaoBKO
          key={item.id}
          item={item}
          pedidoId={pedidoId}
          todosItens={itens} // Passar todos os itens para o modal
          onSuccess={onSuccess}
          size="sm"
        />
      ))}
    </div>
  );
}
